/**
 * App-host destination path rules shared by the tRPC writer, SSR middleware,
 * and composer. Keep this import-free and root-resolvable, like
 * `blog/reservedSlugs.ts`, so both the Functions and app bundles can use it.
 */

export const APP_HOST_PREFIX_MAX = 200;

export const APP_HOST_RESERVED_FIRST_SEGMENTS: ReadonlySet<string> = new Set([
  "404", "500", "about", "account-deleted",
  "agents", "api", "approve", "approve-link", "assets",
  "blog", "dashboard", "docs", "faq",
  "linkedin-callback", "login", "onboarding",
  "platform", "preview", "pricing", "team-login", "waitlist",
]);

const SEGMENT_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isAppHostPathSegment(value: string): boolean {
  return SEGMENT_PATTERN.test(value);
}

export function normalizeAppHostPrefix(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "")
    .replace(/\/{2,}/g, "/");
}

export function appHostPrefixProblem(prefix: string): string | null {
  const trimmed = prefix.trim().toLowerCase().replace(/^\/+|\/+$/g, "");
  const normalized = normalizeAppHostPrefix(prefix);
  if (!normalized) return "Enter a path, like guides or learn/email";
  if (normalized.length > APP_HOST_PREFIX_MAX) {
    return "Keep the path under 200 characters";
  }
  if (!trimmed.split("/").every(isAppHostPathSegment)) {
    return "Use lowercase letters, digits and hyphens, separated by slashes";
  }
  const first = normalized.split("/", 1)[0];
  if (APP_HOST_RESERVED_FIRST_SEGMENTS.has(first)) {
    return `"${first}" is reserved by goodsender.com — pick another first segment`;
  }
  return null;
}

export function appHostPostPath(prefix: string, slug: string): string {
  return `/${prefix}/${slug}`;
}

export function splitAppHostPath(pathname: string):
  | { prefix: string; slug: string; trailingSlash: boolean }
  | undefined {
  if (
    !pathname.startsWith("/") ||
    pathname.includes("?") ||
    pathname.includes("#")
  ) {
    return undefined;
  }
  const trailingSlash = pathname.endsWith("/");
  const parts = pathname.slice(1, trailingSlash ? -1 : undefined).split("/");
  if (parts.length < 2 || !parts.every(isAppHostPathSegment)) return undefined;
  const [first] = parts;
  if (!first || APP_HOST_RESERVED_FIRST_SEGMENTS.has(first)) return undefined;
  const slug = parts.at(-1)!;
  return { prefix: parts.slice(0, -1).join("/"), slug, trailingSlash };
}

/**
 * Prefixes goodsender.com serves a hub page for, at `/<prefix>`, keyed to the
 * hub's name. An app-host article's breadcrumb and category chip point at its
 * hub, so a prefix belongs here only once its hub route exists — otherwise the
 * link is a 404. `/compare` is `src/routes/_home/compare.tsx`.
 */
export const APP_HOST_HUBS: ReadonlyMap<string, string> = new Map([
  ["compare", "Compare"],
]);

/** The hub an app-host article URL sits under, or undefined when it has none. */
export function appHostHubFor(
  articleUrl: string,
): { name: string; url: string } | undefined {
  // A regex, not `URL`: this module stays global-free for both bundles.
  const parts = /^(https?:\/\/[^/?#]+)(\/[^?#]*)/.exec(articleUrl);
  const match = parts ? splitAppHostPath(parts[2]) : undefined;
  const name = match ? APP_HOST_HUBS.get(match.prefix) : undefined;
  if (!parts || !match || !name) return undefined;
  return { name, url: `${parts[1]}/${match.prefix}` };
}
